<script>
    import { createEventDispatcher } from "svelte";
    import { get } from "svelte/store";
    import { fade, scale } from "svelte/transition";
    import toast from "svelte-french-toast";
    import {
        salesSearch,
        salesItemGroupTree,
        fetchSalesItemGroupTree
    } from "./store.js";

    export let isOpen = false;
    const dispatch = createEventDispatcher();

    // 제품유형(MLSFC) > 품목그룹(ITEM_GROUP) > 기종(MHRLS_KND) 3단계 동적 트리
    let treeData = [];
    let isTreeLoading = false;

    function buildTree(rows) {
        if (!rows || rows.length === 0) return [];

        // 제품유형 > 품목그룹 > 기종 계층으로 그룹핑 (조회 순서 유지)
        const ptMap = new Map();
        rows.forEach(r => {
            const ptCode = String(r.mlsfc_code || "").trim();
            const grpCode = String(r.item_group_code || "").trim();
            const mdlCode = String(r.mhrls_knd_code || "").trim();
            if (!grpCode) return;

            if (!ptMap.has(ptCode)) {
                ptMap.set(ptCode, { code: ptCode, name: String(r.mlsfc_nm || "").trim(), groups: new Map() });
            }
            const pt = ptMap.get(ptCode);
            if (!pt.groups.has(grpCode)) {
                pt.groups.set(grpCode, { code: grpCode, name: String(r.item_group_nm || "").trim(), models: new Set() });
            }
            if (mdlCode) pt.groups.get(grpCode).models.add(mdlCode);
        });

        const ptNodes = [...ptMap.values()].map(pt => {
            const children = [...pt.groups.values()].map(grp => ({
                id: `GRP_${pt.code}|${grp.code}`,
                code: grp.code,
                name: grp.name,
                label: `${grp.name || grp.code}`,
                type: "itemGroup",
                checked: false,
                indeterminate: false,
                expanded: false,
                children: [...grp.models].map(mdl => ({
                    id: `MDL_${pt.code}|${grp.code}|${mdl}`,
                    code: mdl,
                    groupCode: grp.code,
                    name: mdl,
                    label: `${mdl}`,
                    type: "model",
                    checked: false,
                    indeterminate: false,
                    children: []
                }))
            }));

            return {
                id: `PT_${pt.code}`,
                code: pt.code,
                name: pt.name,
                label: `${pt.name || pt.code || "미분류"}`,
                type: "productType",
                expanded: true,
                checked: false,
                indeterminate: false,
                children
            };
        });

        return [
            {
                id: "ROOT",
                name: "ALL",
                code: "",
                label: "ALL (전체 제품)",
                type: "root",
                expanded: true,
                checked: false,
                indeterminate: false,
                children: ptNodes
            }
        ];
    }

    // 하위 노드 상태로부터 상위 노드의 체크/부분체크 상태를 재계산 (0: 없음, 1: 일부, 2: 전체)
    function recompute(node) {
        if (!node.children || node.children.length === 0) {
            node.indeterminate = false;
            return node.checked ? 2 : 0;
        }
        let all = true;
        let any = false;
        node.children.forEach(child => {
            const s = recompute(child);
            if (s !== 2) all = false;
            if (s !== 0) any = true;
        });
        node.checked = all;
        node.indeterminate = any && !all;
        return all ? 2 : any ? 1 : 0;
    }

    function refreshTree() {
        if (!treeData || treeData.length === 0) return;
        recompute(treeData[0]);
        treeData = [...treeData];
    }

    // 하위 자식 노드 재귀 체크 설정
    function setCheckRecursive(node, checked) {
        node.checked = checked;
        node.indeterminate = false;
        if (node.children && node.children.length > 0) {
            node.children.forEach(child => setCheckRecursive(child, checked));
        }
    }

    // 노드 클릭 토글 (ALL / 제품유형 / 품목그룹 / 기종 공통)
    function toggleNode(node) {
        setCheckRecursive(node, !node.checked);
        refreshTree();
    }

    // 펼침/접힘 토글
    function toggleExpand(node) {
        node.expanded = !node.expanded;
        treeData = [...treeData];
    }

    // 모두 열기: 제품유형 + 품목그룹 하위(기종)까지 전부 펼침
    function expandAll() {
        if (!treeData || !treeData[0]) return;
        treeData[0].expanded = true;
        treeData[0].children.forEach(pt => {
            pt.expanded = true;
            pt.children.forEach(grp => {
                grp.expanded = true;
            });
        });
        treeData = [...treeData];
    }

    // 모두 닫기: 제품유형까지만 보이도록 접음
    function collapseAll() {
        if (!treeData || !treeData[0]) return;
        treeData[0].expanded = true;
        treeData[0].children.forEach(pt => {
            pt.expanded = false;
            pt.children.forEach(grp => {
                grp.expanded = false;
            });
        });
        treeData = [...treeData];
    }

    // 전체 선택 (모두 체크)
    function checkAll() {
        if (treeData && treeData[0]) {
            setCheckRecursive(treeData[0], true);
            refreshTree();
        }
    }

    // 다시 열었을 때: 체크(전체/일부)된 품목그룹은 기종까지 펼쳐서 선택 상태를 바로 확인할 수 있게 함
    function expandPartialGroups() {
        if (!treeData || !treeData[0]) return;
        treeData[0].expanded = true;
        treeData[0].children.forEach(pt => {
            if (pt.checked || pt.indeterminate) pt.expanded = true;
            pt.children.forEach(grp => {
                if (grp.checked || grp.indeterminate) {
                    grp.expanded = true;
                    pt.expanded = true;
                }
            });
        });
        treeData = [...treeData];
    }

    let wasOpen = false;

    // 모달이 열리는 순간(false -> true)에만 트리를 초기화하고 선택 상태를 복원
    $: if (isOpen && !wasOpen) {
        wasOpen = true;
        initModalTree();
    } else if (!isOpen && wasOpen) {
        wasOpen = false;
    }

    async function initModalTree() {
        let rows = get(salesItemGroupTree);
        if (!rows || rows.length === 0) {
            isTreeLoading = true;
            rows = await fetchSalesItemGroupTree();
            isTreeLoading = false;
        }
        treeData = buildTree(rows);
        syncChecksFromSearch();
    }

    // 저장된 선택(체크한 노드 ID)이 없으면 전체 선택, 있으면 복원
    function syncChecksFromSearch() {
        if (!treeData || treeData.length === 0) return;
        const ids = get(salesSearch).treeCheckedIds || [];
        if (ids.length === 0) {
            checkAll();
            return;
        }
        const idSet = new Set(ids);
        (function apply(node) {
            node.checked = idSet.has(node.id);
            if (node.children) node.children.forEach(apply);
        })(treeData[0]);
        refreshTree();
        expandPartialGroups();
    }

    function handleClose() {
        dispatch("close");
    }

    // [적용] 버튼 클릭 (이미 조회된 데이터에서 바로 필터링되므로 재조회 없음)
    function handleApply() {
        const root = treeData[0];
        if (!root) {
            dispatch("close");
            return;
        }

        if (root.checked) {
            // 전체 선택 -> 필터 해제
            salesSearch.update(s => ({
                ...s,
                treeCheckedIds: [],
                selectedItemGroups: [],
                selectedGroupModels: [],
            }));
            toast.success("전체 제품이 선택되었습니다.", { duration: 1500 });
            dispatch("close");
            return;
        }

        const checkedIds = [];
        (function collect(node) {
            if (node.checked) checkedIds.push(node.id);
            if (node.children) node.children.forEach(collect);
        })(root);

        const fullGrpCodes = [];   // 하위 기종 전체가 선택된 품목그룹
        const pairs = [];          // 일부 기종만 선택된 품목그룹의 "품목그룹|기종"
        root.children.forEach(pt => {
            pt.children.forEach(grp => {
                if (grp.checked) {
                    fullGrpCodes.push(grp.code);
                } else if (grp.indeterminate) {
                    grp.children.forEach(mdl => {
                        if (mdl.checked) pairs.push(`${grp.code}|${mdl.code}`);
                    });
                }
            });
        });

        if (fullGrpCodes.length === 0 && pairs.length === 0) {
            toast.error("최소 1개 이상의 분류를 선택하거나 전체를 선택해주세요.");
            return;
        }

        salesSearch.update(s => ({
            ...s,
            treeCheckedIds: checkedIds,
            selectedItemGroups: fullGrpCodes,
            selectedGroupModels: pairs,
        }));
        toast.success("선택한 제품 분류가 적용되었습니다.", { duration: 1500 });
        dispatch("close");
    }

    // 선택된 항목 개수 (전체 선택된 품목그룹은 1건, 일부 선택된 품목그룹은 선택된 기종 수만큼)
    $: selectedCount = (() => {
        if (!treeData || !treeData[0]) return 0;
        if (treeData[0].checked) return "전체";
        let count = 0;
        treeData[0].children.forEach(pt => {
            pt.children.forEach(grp => {
                if (grp.checked) count++;
                else if (grp.indeterminate) count += grp.children.filter(m => m.checked).length;
            });
        });
        return count;
    })();
</script>

{#if isOpen}
    <div class="modal-backdrop" on:click={handleClose} transition:fade={{ duration: 180 }}>
        <div class="modal-box" on:click|stopPropagation transition:scale={{ duration: 200, start: 0.95 }}>
            <div class="modal-header">
                <div class="modal-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                        <polyline points="2 17 12 22 22 17"/>
                        <polyline points="2 12 12 17 22 12"/>
                    </svg>
                    <h3>제품 분류 트리 선택</h3>
                    <span class="tree-sub-desc">(제품유형 ➔ 품목그룹 ➔ 기종)</span>
                </div>
                <button class="close-btn" on:click={handleClose} title="닫기">✕</button>
            </div>

            <div class="modal-body">
                {#if isTreeLoading}
                    <div class="tree-empty">제품 분류를 불러오는 중입니다...</div>
                {:else if treeData.length === 0}
                    <div class="tree-empty">조회된 제품 분류가 없습니다.</div>
                {/if}
                <div class="tree-container">
                    {#each treeData as root}
                        <div class="tree-root">
                            <div
                                class="tree-node root-node"
                                class:selected={root.checked}
                                on:click={() => toggleNode(root)}
                            >
                                <div
                                    class="custom-checkbox"
                                    class:checked={root.checked}
                                    class:indeterminate={root.indeterminate}
                                ></div>
                                <span class="tree-icon">📂</span>
                                <span class="tree-label bold">{root.label}</span>
                            </div>

                            <div class="tree-children">
                                {#each root.children as pt}
                                    <div class="tree-cat-group">
                                        <div
                                            class="tree-node cat-node"
                                            class:selected={pt.checked}
                                            on:click={() => toggleNode(pt)}
                                        >
                                            {#if pt.children.length > 0}
                                                <button type="button" class="expand-btn" on:click|stopPropagation={() => toggleExpand(pt)} title={pt.expanded ? "접기" : "펼치기"}>
                                                    {pt.expanded ? "▾" : "▸"}
                                                </button>
                                            {:else}
                                                <span class="expand-spacer"></span>
                                            {/if}
                                            <div
                                                class="custom-checkbox"
                                                class:checked={pt.checked}
                                                class:indeterminate={pt.indeterminate}
                                            ></div>
                                            <span class="tree-icon">📁</span>
                                            <span class="tree-label">{pt.label}</span>
                                        </div>

                                        {#if pt.expanded}
                                            <div class="tree-children">
                                                {#each pt.children as grp}
                                                    <div
                                                        class="tree-node grp-node"
                                                        class:selected={grp.checked}
                                                        on:click={() => toggleNode(grp)}
                                                    >
                                                        {#if grp.children.length > 0}
                                                            <button type="button" class="expand-btn" on:click|stopPropagation={() => toggleExpand(grp)} title={grp.expanded ? "기종 접기" : "기종 펼치기"}>
                                                                {grp.expanded ? "▾" : "▸"}
                                                            </button>
                                                        {:else}
                                                            <span class="expand-spacer"></span>
                                                        {/if}
                                                        <div
                                                            class="custom-checkbox"
                                                            class:checked={grp.checked}
                                                            class:indeterminate={grp.indeterminate}
                                                        ></div>
                                                        <span class="tree-icon">📦</span>
                                                        <span class="tree-label">{grp.label}</span>
                                                        {#if grp.children.length > 0}
                                                            <span class="child-count">{grp.children.length}</span>
                                                        {/if}
                                                    </div>

                                                    {#if grp.expanded && grp.children.length > 0}
                                                        <div class="tree-children">
                                                            {#each grp.children as mdl}
                                                                <div
                                                                    class="tree-node mdl-node"
                                                                    class:selected={mdl.checked}
                                                                    on:click={() => toggleNode(mdl)}
                                                                >
                                                                    <div
                                                                        class="custom-checkbox"
                                                                        class:checked={mdl.checked}
                                                                    ></div>
                                                                    <span class="tree-icon">🚜</span>
                                                                    <span class="tree-label">{mdl.label}</span>
                                                                </div>
                                                            {/each}
                                                        </div>
                                                    {/if}
                                                {/each}
                                            </div>
                                        {/if}
                                    </div>
                                {/each}
                            </div>
                        </div>
                    {/each}
                </div>
            </div>

            <div class="modal-footer">
                <div class="footer-left">
                    <button type="button" class="tool-btn" on:click={expandAll} title="모든 분류 펼치기">▾ 모두 열기</button>
                    <button type="button" class="tool-btn" on:click={collapseAll} title="제품유형까지만 보이도록 접기">▸ 모두 닫기</button>
                    {#if selectedCount !== 0}
                        <span class="selection-badge">선택됨: <strong>{selectedCount}</strong>건</span>
                    {/if}
                </div>
                <div class="footer-right">
                    <button type="button" class="btn btn-secondary" on:click={handleClose}>닫기</button>
                    <button type="button" class="btn btn-primary" on:click={handleApply}>적용</button>
                </div>
            </div>
        </div>
    </div>
{/if}

<style>
    .modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.65);
        backdrop-filter: blur(2px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2000;
        padding: 16px;
    }
    .modal-box {
        background: white;
        width: 100%;
        max-width: 640px;
        border-radius: 12px;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.08);
        overflow: hidden;
        display: flex;
        flex-direction: column;
        border: 1px solid #cbd5e1;
    }
    .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 20px;
        border-bottom: 1px solid #e2e8f0;
        background: #f8fafc;
    }
    .modal-title {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #0f172a;
    }
    .modal-title svg {
        color: #2563eb;
    }
    .modal-title h3 {
        margin: 0;
        font-size: 1rem;
        font-weight: 800;
        letter-spacing: -0.02em;
    }
    .tree-sub-desc {
        font-size: 0.78rem;
        color: #64748b;
        font-weight: 600;
    }
    .close-btn {
        background: none;
        border: none;
        font-size: 1.15rem;
        color: #94a3b8;
        cursor: pointer;
        padding: 2px 6px;
        line-height: 1;
        border-radius: 4px;
        transition: all 0.15s;
    }
    .close-btn:hover {
        color: #0f172a;
        background: #e2e8f0;
    }
    .modal-body {
        padding: 8px 14px;
        height: min(86vh, 900px);
        overflow-y: auto;
    }
    .tree-empty {
        padding: 18px 0;
        text-align: center;
        font-size: 0.85rem;
        color: #64748b;
        font-weight: 600;
    }
    .tool-btn {
        padding: 6px 12px;
        font-size: 0.82rem;
        font-weight: 700;
        color: #475569;
        background: #ffffff;
        border: 1px solid #cbd5e1;
        border-radius: 5px;
        cursor: pointer;
        transition: all 0.15s;
    }
    .tool-btn:hover {
        background: #eff6ff;
        border-color: #93c5fd;
        color: #1d4ed8;
    }
    .tree-container {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
    .tree-node {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 2px 8px;
        border-radius: 5px;
        cursor: pointer;
        transition: all 0.15s;
        user-select: none;
    }
    .tree-node:hover {
        background: #f1f5f9;
    }
    .root-node {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        font-size: 0.85rem;
        margin-bottom: 2px;
    }
    .root-node:hover {
        background: #eff6ff;
        border-color: #bfdbfe;
    }
    .cat-node {
        font-weight: 700;
        color: #1e293b;
        font-size: 0.82rem;
    }
    .cat-node:hover {
        background: #eff6ff;
        color: #1d4ed8;
    }
    .grp-node {
        font-size: 0.78rem;
        font-weight: 600;
        color: #334155;
    }
    .grp-node:hover {
        background: #eff6ff;
        color: #2563eb;
    }
    .mdl-node {
        font-size: 0.75rem;
        font-weight: 500;
        color: #475569;
        padding: 1px 8px;
    }
    .mdl-node:hover {
        background: #eff6ff;
        color: #2563eb;
    }
    .tree-children {
        padding-left: 14px;
        display: flex;
        flex-direction: column;
        gap: 1px;
        border-left: 1px dashed #cbd5e1;
        margin-left: 10px;
        margin-top: 1px;
        margin-bottom: 2px;
    }

    /* 펼침/접힘 버튼 */
    .expand-btn {
        flex-shrink: 0;
        width: 22px;
        height: 20px;
        padding: 0;
        margin-right: 4px;
        border: 1px solid #cbd5e1;
        background: #f1f5f9;
        color: #475569;
        font-size: 0.8rem;
        line-height: 1;
        cursor: pointer;
        border-radius: 5px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s;
    }
    .expand-btn:hover {
        background: #dbeafe;
        border-color: #60a5fa;
        color: #1d4ed8;
    }
    .expand-btn:active {
        transform: scale(0.94);
    }
    .expand-spacer {
        flex-shrink: 0;
        width: 22px;
        height: 20px;
        margin-right: 4px;
    }
    .child-count {
        font-size: 0.68rem;
        font-weight: 700;
        color: #64748b;
        background: #e2e8f0;
        padding: 0 7px;
        border-radius: 10px;
        pointer-events: none;
    }

    .tree-icon {
        font-size: 0.95rem;
        line-height: 1;
        pointer-events: none;
    }
    .tree-label {
        flex: 1;
        letter-spacing: -0.01em;
        pointer-events: none;
    }
    .bold {
        font-weight: 700;
        color: #0f172a;
    }

    /* 커스텀 체크박스 (클릭 영역은 상위 .tree-node가 담당) */
    .custom-checkbox {
        position: relative;
        flex-shrink: 0;
        width: 15px;
        height: 15px;
        background-color: #ffffff;
        border: 1.5px solid #94a3b8;
        border-radius: 4px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s ease;
        pointer-events: none;
    }
    .tree-node:hover .custom-checkbox {
        border-color: #3b82f6;
    }
    .custom-checkbox.checked,
    .custom-checkbox.indeterminate {
        background-color: #2563eb;
        border-color: #2563eb;
    }
    .custom-checkbox.checked::after {
        content: "";
        width: 4px;
        height: 8px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
        margin-bottom: 2px;
    }
    .custom-checkbox.indeterminate::after {
        content: "";
        width: 8px;
        height: 2px;
        background-color: white;
        border-radius: 1px;
    }

    /* 푸터 스타일 */
    .modal-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 20px;
        border-top: 1px solid #e2e8f0;
        background: #f8fafc;
    }
    .footer-left {
        display: flex;
        align-items: center;
        gap: 12px;
    }
    .footer-right {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .selection-badge {
        font-size: 0.8rem;
        color: #475569;
    }
    .selection-badge strong {
        color: #2563eb;
        font-weight: 800;
    }

    .btn {
        padding: 7px 16px;
        border-radius: 6px;
        font-size: 0.84rem;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s;
        line-height: 1.3;
    }
    .btn-secondary {
        background: #ffffff;
        border: 1px solid #cbd5e1;
        color: #334155;
    }
    .btn-secondary:hover {
        background: #f1f5f9;
        border-color: #94a3b8;
    }
    .btn-primary {
        background: #2563eb;
        border: 1px solid #1d4ed8;
        color: white;
        box-shadow: 0 1px 2px rgba(37, 99, 235, 0.2);
    }
    .btn-primary:hover:not(:disabled) {
        background: #1d4ed8;
    }
    .btn-primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
</style>
